import { useEffect, useRef, useState, useCallback } from 'react'
import './VoiceChat.css'
import LevelMeter from './LevelMeter'
import { createPcmPlayer, type PcmPlayer } from '../lib/pcmPlayer'

// ── Types matching the server message protocol ────────────────────────────────
type ServerMessage =
  | { type: 'transcript'; text: string; isFinal: boolean }
  | { type: 'token';      text: string }
  | { type: 'done' }
  | { type: 'error';      message: string; code?: 'link_used' }
  | { type: 'metrics';    data: TurnMetrics }
  | { type: 'hangup' }
  | { type: 'barge-in' }
  | { type: 'history';    messages: { role: 'user' | 'assistant'; content: string }[] }

export interface TurnMetrics {
  turn:               number
  sttEndMs:           number   // last audio → Deepgram final  (VAD wait + network)
  llmTtftMs:          number   // transcript → first Grok token (LLM queue / cold-start)
  llmTotalMs:         number   // transcript → last Grok token  (full generation)
  ttsTtftMs:          number   // first TTS request → first audio sent  (TTS cold-start)
  ttsTotalMs:         number   // first TTS request → last audio sent   (TTS throughput)
  e2eMs:              number   // transcript → last audio  (perceived latency)
  responseChars:      number
  pipelineSentences:  number   // parallel TTS requests fired during LLM streaming
  pipelineSavingMs:   number   // overlap: how early TTS started vs sequential
}

type SessionStatus = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

interface Turn {
  role: 'user' | 'assistant'
  text: string
}

const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined)
  ?? (window.location.protocol === 'https:'
    ? `wss://${window.location.host}/ws/voice`
    : `ws://${window.location.hostname}:3000/ws/voice`)

// ── Ready tone (Star Trek-style two-note ascending chime via Web Audio) ──────
function playReadyTone(ctx: AudioContext) {
  const notes = [
    { t: 0.0,  freq: 880,  dur: 0.12 },   // A5
    { t: 0.15, freq: 1318, dur: 0.18 },   // E6
  ]
  for (const { t, freq, dur } of notes) {
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.value = freq
    const start = ctx.currentTime + t
    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(0.18, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur)
    osc.start(start)
    osc.stop(start + dur + 0.05)
  }
}

// ── Hangup tone (descending two-note, reverse of ready tone) ─────────────────
function playHangupTone(ctx: AudioContext) {
  const notes = [
    { t: 0.0,  freq: 1318, dur: 0.12 },   // E6 → descending
    { t: 0.15, freq: 660,  dur: 0.22 },   // E5 → fade out
  ]
  for (const { t, freq, dur } of notes) {
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.value = freq
    const start = ctx.currentTime + t
    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(0.18, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur)
    osc.start(start)
    osc.stop(start + dur + 0.05)
  }
}

interface VoiceChatProps {
  /**
   * Runs when the user starts a call, before the mic and socket open — e.g. to
   * redeem a magic link only when the employee actually starts. Throwing aborts
   * the start and shows the error message.
   */
  beforeStart?: () => Promise<void>
  /** Employee session: the server refused the call because the link's one session is spent. */
  onLinkUsed?: (message: string) => void
  /** Called with true while a session is connecting/active, false when idle. */
  onActiveChange?: (active: boolean) => void
}

/**
 * The voice chat. Sessions open only from an employee magic link: the server
 * identifies (and locks) the employee from the employee_session cookie.
 */
export default function VoiceChat({ beforeStart, onLinkUsed, onActiveChange }: VoiceChatProps) {
  const [status, setStatus]             = useState<SessionStatus>('idle')
  const [transcript, setTranscript]     = useState('')      // live interim transcript
  const [responseText, setResponseText] = useState('')      // streaming Grok response
  const [turns, setTurns]               = useState<Turn[]>([])
  const [errorMsg, setErrorMsg]         = useState('')
  const [micAnalyser, setMicAnalyser]         = useState<AnalyserNode | null>(null)
  const [speakerAnalyser, setSpeakerAnalyser] = useState<AnalyserNode | null>(null)
  const [lastMetrics, setLastMetrics]         = useState<TurnMetrics | null>(null)

  const wsRef               = useRef<WebSocket | null>(null)
  const mediaRecorderRef    = useRef<MediaRecorder | null>(null)
  const audioContextRef     = useRef<AudioContext | null>(null)
  const speakerAnalyserRef  = useRef<AnalyserNode | null>(null)
  // Streamed PCM speech, scheduled gaplessly (lib/pcmPlayer.ts)
  const playerRef           = useRef<PcmPlayer | null>(null)
  // Refs for stale-closure safety — onmessage is set once but reads these during the session
  const currentTranscriptRef = useRef('')
  const currentResponseRef   = useRef('')
  const chatEndRef           = useRef<HTMLDivElement | null>(null)
  // Turn-completion tracking: 'done' msg and Blob.arrayBuffer() are both async;
  // we only transition back to 'listening' when BOTH have settled.
  const turnDoneRef     = useRef(false)   // server sent 'done'
  const pendingBlobsRef = useRef(0)       // blobs received but arrayBuffer() not yet resolved
  const responseCommittedRef = useRef(false) // 'done' received — responseText already in turns
  const hangupPendingRef = useRef(false)  // AI sent end_session — auto-close when audio drains
  // Stable ref to stopSession — avoids circular dependency in checkTurnComplete
  const stopSessionRef  = useRef<() => void>(() => {})
  // Watchdog: if stuck in thinking/speaking for >30s, force back to listening
  const watchdogRef     = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // ── Turn completion guard ─────────────────────────────────────────────────
  // Transitions to 'listening' only when ALL of these are true:
  //   • server sent 'done'   (turnDoneRef)
  //   • all Blobs resolved   (pendingBlobsRef === 0)
  //   • all scheduled speech has finished playing (player not busy)
  const checkTurnComplete = useCallback(() => {
    if (
      turnDoneRef.current &&
      pendingBlobsRef.current === 0 &&
      !(playerRef.current?.busy ?? false)
    ) {
      clearTimeout(watchdogRef.current)
      turnDoneRef.current = false
      if (hangupPendingRef.current) {
        hangupPendingRef.current = false
        setTimeout(() => stopSessionRef.current(), 600)
        return
      }
      setStatus('listening')
    }
  }, [])

  // ── Auto-scroll chat to bottom whenever content changes ────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns, responseText, transcript])

  // ── Connect / start session ───────────────────────────────────────────────
  const startSession = useCallback(async () => {
    setStatus('connecting')
    setErrorMsg('')
    setTurns([])
    setTranscript('')
    setResponseText('')
    currentTranscriptRef.current = ''
    currentResponseRef.current   = ''
    turnDoneRef.current          = false
    pendingBlobsRef.current      = 0
    responseCommittedRef.current = false
    playerRef.current?.stop()
    clearTimeout(watchdogRef.current)

    if (beforeStart) {
      try {
        await beforeStart()
      } catch (err) {
        setStatus('error')
        setErrorMsg(err instanceof Error ? err.message : 'Could not start the session.')
        return
      }
    }

    // Request mic permission — explicit constraints ensure the browser's built-in
    // audio processing pipeline normalises gain, suppresses background noise, and
    // removes echo before any audio reaches Deepgram's VAD.  Without these, a
    // high-gain mic can produce a signal that Deepgram treats as continuous speech,
    // preventing speech_final from ever firing.
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl:  true,   // normalise mic level regardless of hardware sensitivity
          noiseSuppression: true,   // filter background noise that could trigger VAD
          echoCancellation: true,   // prevent TTS speaker output from feeding back into STT
          // NOTE: do NOT set sampleRate here — forcing a different rate than the
          // AudioContext output (44.1/48 kHz) breaks the browser's AEC, causing
          // the AI's TTS audio to feed back into the mic as user input.
        },
      })
    } catch {
      setStatus('error')
      setErrorMsg('Microphone access denied.')
      return
    }

    // ── Audio context + level analysers ──────────────────────────────────────
    const audioCtx = new AudioContext()
    audioContextRef.current = audioCtx

    // Mic analyser: tap the input stream (NOT connected to destination – no monitor)
    const micAn = audioCtx.createAnalyser()
    micAn.fftSize = 1024
    audioCtx.createMediaStreamSource(stream).connect(micAn)
    setMicAnalyser(micAn)

    // Speaker analyser: sits between source nodes and the destination
    const spkAn = audioCtx.createAnalyser()
    spkAn.fftSize = 1024
    spkAn.connect(audioCtx.destination)
    speakerAnalyserRef.current = spkAn
    setSpeakerAnalyser(spkAn)

    // Speech arrives as PCM16 frames while the AI is still replying; play them back-to-back.
    const player = createPcmPlayer(audioCtx, spkAn)
    player.onIdle = () => checkTurnComplete()
    playerRef.current = player

    const ws = new WebSocket(WS_URL)
    wsRef.current = ws

    ws.onopen = () => {
      setStatus('listening')
      ws.send(JSON.stringify({ type: 'start' }))

      // Play the "ready" chime — user speaks first (says "hello"), then the AI responds.
      // This mirrors a real incoming phone call: the callee picks up and says hello,
      // the caller (AI) then identifies itself.
      playReadyTone(audioCtx)

      // Start streaming audio chunks as binary frames
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          e.data.arrayBuffer().then((buf) => ws.send(buf))
        }
      }
      recorder.start(250)  // 250ms chunks
    }

    ws.onmessage = (event) => {
      // Binary frame = TTS audio chunk
      if (event.data instanceof Blob) {
        pendingBlobsRef.current++
        event.data.arrayBuffer()
          .then((buf) => {
            pendingBlobsRef.current--
            playerRef.current?.play(buf)
            setStatus('speaking')
          })
          .catch(() => {
            // arrayBuffer() failed — decrement so checkTurnComplete can still fire
            pendingBlobsRef.current--
            checkTurnComplete()
          })
        return
      }

      const msg = JSON.parse(event.data as string) as ServerMessage

      switch (msg.type) {
        case 'history':
          // Pre-populate chat with the full rolling conversation from DB
          setTurns(msg.messages.map(m => ({ role: m.role, text: m.content })))
          break

        case 'transcript':
          currentTranscriptRef.current = msg.text
          setTranscript(msg.text)
          if (msg.isFinal) {
            // New user turn starting — now safe to clear the previous AI response bubble
            responseCommittedRef.current = false
            setResponseText('')
            setStatus('thinking')
            // Arm watchdog: if stuck in thinking/speaking for >30s, force back to listening
            clearTimeout(watchdogRef.current)
            watchdogRef.current = setTimeout(() => {
              console.warn('[voice] watchdog: stuck in thinking/speaking — forcing listening')
              turnDoneRef.current  = false
              pendingBlobsRef.current = 0
              playerRef.current?.stop()
              setStatus('listening')
            }, 30_000)
          }
          break

        case 'token':
          responseCommittedRef.current = false
          currentResponseRef.current += msg.text
          setResponseText(currentResponseRef.current)
          break

        case 'done': {
          // Capture ref values BEFORE clearing them — React's functional updater
          // runs lazily during reconciliation, AFTER synchronous code below has
          // already zeroed the refs. Reading inside the updater would always get ''.
          const committedUser = currentTranscriptRef.current
          const committedAI   = currentResponseRef.current
          currentTranscriptRef.current = ''
          currentResponseRef.current   = ''
          setTranscript('')
          setTurns((prev) => {
            // Skip turns where both sides are empty (e.g. silent greet turn)
            if (!committedUser && !committedAI) return prev
            return [
              ...prev,
              ...(committedUser ? [{ role: 'user'      as const, text: committedUser }] : []),
              ...(committedAI   ? [{ role: 'assistant' as const, text: committedAI   }] : []),
            ]
          })
          // Keep responseText visible during TTS playback — it will be cleared
          // at the start of the next user turn so the text never disappears mid-audio.
          // setResponseText('') intentionally deferred.
          responseCommittedRef.current = true
          turnDoneRef.current = true
          checkTurnComplete()
          break
        }

        case 'metrics':
          setLastMetrics(msg.data)
          break

        case 'error':
          setStatus('error')
          setErrorMsg(msg.message)
          if (msg.code === 'link_used') onLinkUsed?.(msg.message)
          break

        case 'hangup':
          // AI has finished its closing message — wait for audio queue to drain
          // then auto-end the call (same as user clicking End Call)
          hangupPendingRef.current = true
          checkTurnComplete()
          break

        case 'barge-in':
          // User interrupted the AI — flush all queued audio immediately
          console.log('[voice] barge-in: flushing audio queue')
          playerRef.current?.stop()
          pendingBlobsRef.current = 0
          turnDoneRef.current = false
          responseCommittedRef.current = false
          currentResponseRef.current = ''
          setResponseText('')
          setStatus('listening')
          clearTimeout(watchdogRef.current)
          break
      }
    }

    ws.onclose = () => {
      setStatus('idle')
      stream.getTracks().forEach((t) => t.stop())
    }

    ws.onerror = () => {
      setStatus('error')
      setErrorMsg('WebSocket connection failed. Is the backend running?')
    }
  }, [checkTurnComplete, beforeStart, onLinkUsed])

  // ── Stop session ─────────────────────────────────────────────────────────
  const stopSession = useCallback(() => {
    clearTimeout(watchdogRef.current)
    mediaRecorderRef.current?.stop()
    wsRef.current?.send(JSON.stringify({ type: 'stop' }))
    wsRef.current?.close()
    wsRef.current = null
    speakerAnalyserRef.current = null
    playerRef.current?.stop()
    playerRef.current = null
    setMicAnalyser(null)
    setSpeakerAnalyser(null)
    setStatus('idle')
    // Play hangup tone then close the AudioContext after it finishes
    const ctx = audioContextRef.current
    audioContextRef.current = null
    if (ctx) {
      playHangupTone(ctx)
      setTimeout(() => ctx.close(), 500)
    }
  }, [])
  // Keep the ref in sync so checkTurnComplete can call it without a dependency cycle
  stopSessionRef.current = stopSession

  // ── Text-only shortcut (no mic) ───────────────────────────────────────────
  const [textInput, setTextInput] = useState('')
  const sendText = useCallback(() => {
    if (!textInput.trim() || !wsRef.current) return
    const text = textInput.trim()
    currentTranscriptRef.current = text   // pre-set so 'done' handler has the text
    wsRef.current.send(JSON.stringify({ type: 'text', text }))
    setTextInput('')
    setStatus('thinking')
  }, [textInput])

  // Cleanup on unmount
  useEffect(() => () => { stopSession() }, [stopSession])

  // Let the page lock the employee selector while a session is running
  useEffect(() => {
    onActiveChange?.(status !== 'idle' && status !== 'error')
  }, [status, onActiveChange])

  // Auto-scroll chat to bottom on every update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns, transcript, responseText])

  const statusLabel: Record<SessionStatus, string> = {
    idle:       '● Idle',
    connecting: '◌ Connecting…',
    listening:  '🎙 Listening…',
    thinking:   '⏳ Thinking…',
    speaking:   '🔊 Speaking…',
    error:      '⚠ Error',
  }

  return (
    <div className="voice-chat">

      {/* ── Header: status pill + level meters ── */}
      <div className="vc-header">
        <div className={`vc-status vc-status--${status}`}>{statusLabel[status]}</div>
        <div className="vc-meters">
          <LevelMeter analyser={micAnalyser}     label="Mic"     icon="🎙" />
          <LevelMeter analyser={speakerAnalyser} label="Speaker" icon="🔊" />
        </div>
      </div>

      {errorMsg && <p className="vc-error">{errorMsg}</p>}

      {/* ── Chat window ── */}
      <div className="vc-chat">
        {turns.length === 0 && !transcript && status === 'idle' && (
          <p className="vc-chat__empty">Start a voice session or type a message to begin.</p>
        )}

        {turns.map((t, i) => (
          <div key={i} className={`vc-bubble vc-bubble--${t.role}`}>
            <span className="vc-bubble__who">{t.role === 'user' ? 'You' : 'AI'}</span>
            <span className="vc-bubble__text">{t.text}</span>
          </div>
        ))}

        {/* In-progress: live transcript (interim or final) */}
        {transcript && (
          <div className="vc-bubble vc-bubble--user vc-bubble--live">
            <span className="vc-bubble__who">You</span>
            <span className="vc-bubble__text">{transcript}</span>
          </div>
        )}

        {/* In-progress: streaming AI response (hidden once committed to turns list) */}
        {responseText && !responseCommittedRef.current && (
          <div className="vc-bubble vc-bubble--assistant vc-bubble--live">
            <span className="vc-bubble__who">AI</span>
            <span className="vc-bubble__text">{responseText}<span className="vc-cursor">▌</span></span>
          </div>
        )}

        {/* Thinking indicator — after transcript finalised but before first token */}
        {status === 'thinking' && !responseText && (
          <div className="vc-bubble vc-bubble--assistant vc-bubble--live">
            <span className="vc-bubble__who">AI</span>
            <span className="vc-bubble__text vc-thinking">thinking…</span>
          </div>
        )}

        {lastMetrics && <MetricsPanel m={lastMetrics} />}
        <div ref={chatEndRef} />
      </div>

      {/* ── Footer: text input + mic button ── */}
      <div className="vc-footer">
        <div className="vc-text-input">
          <input
            type="text"
            placeholder={
              status === 'idle' || status === 'error'
                ? 'Start a session first…'
                : 'Type a message or use the mic…'
            }
            value={textInput}
            disabled={status === 'idle' || status === 'error' || status === 'connecting'}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendText()}
          />
          <button
            onClick={sendText}
            disabled={!textInput.trim() || status === 'idle' || status === 'connecting'}
          >
            Send
          </button>
        </div>
        <div className="vc-controls">
          {status === 'idle' || status === 'error' ? (
            <button className="vc-btn vc-btn--start" onClick={() => void startSession()}>
              📞 Accept Call
            </button>
          ) : (
            <button className="vc-btn vc-btn--stop" onClick={stopSession}>
              📵 End Call
            </button>
          )}
        </div>
      </div>

    </div>
  )
}
// ── Latency breakdown panel ──────────────────────────────────────────────────
interface Segment { label: string; ms: number; color: string; tip: string }

function MetricsPanel({ m }: { m: TurnMetrics }) {
  // Bar is scaled to sum of all segments so each stage is visible.
  // e2eMs < segTotal when pipelining is active (TTS/LLM overlap = saving).
  const segments: Segment[] = [
    { label: 'VAD',      ms: m.sttEndMs,                  color: '#60a5fa', tip: 'Last audio → Deepgram final (silence detection + Deepgram RTT)' },
    { label: 'LLM TTFT', ms: m.llmTtftMs,                 color: '#34d399', tip: 'Transcript → first Grok token (LLM queue + cold-start)' },
    { label: 'LLM gen',  ms: m.llmTotalMs - m.llmTtftMs,  color: '#6ee7b7', tip: 'Token generation time (proportional to response length)' },
    { label: 'TTS cold', ms: m.ttsTtftMs,                  color: '#f472b6', tip: 'TTS request → first audio chunk (OpenAI TTS warm-up)' },
    { label: 'TTS strm', ms: m.ttsTotalMs - m.ttsTtftMs,   color: '#f9a8d4', tip: 'TTS streaming duration (proportional to response length)' },
  ]
  const segTotal = Math.max(1, segments.reduce((s, x) => s + Math.max(0, x.ms), 0))
  const savingPct = Math.round((m.pipelineSavingMs / segTotal) * 100)

  return (
    <details className="vc-metrics">
      <summary className="vc-metrics__summary">
        ⏱ Turn {m.turn} · <strong>{m.e2eMs.toLocaleString()} ms</strong> end-to-end
        {m.pipelineSentences > 0 && (
          <span className="vc-metrics__badge"
            title={`TTS started ${m.pipelineSavingMs} ms before LLM finished`}>
            ⚡ {m.pipelineSentences}s · ~{m.pipelineSavingMs.toLocaleString()} ms saved
          </span>
        )}
        <span className="vc-metrics__chars">{m.responseChars} chars</span>
      </summary>

      {/* Proportional stacked bar (relative to segment sum) */}
      <div className="vc-metrics__bar">
        {segments.map((s) => s.ms > 0 && (
          <div
            key={s.label}
            className="vc-metrics__segment"
            style={{ width: `${(Math.max(0, s.ms) / segTotal) * 100}%`, background: s.color }}
            title={`${s.label}: ${s.ms} ms — ${s.tip}`}
          />
        ))}
      </div>
      {m.pipelineSavingMs > 0 && (
        <div className="vc-metrics__overlap-note">
          └ {savingPct}% of LLM gen overlapped with TTS (pipeline saving)
        </div>
      )}

      {/* Legend rows */}
      <div className="vc-metrics__legend">
        {segments.map((s) => (
          <div key={s.label} className="vc-metrics__row" title={s.tip}>
            <span className="vc-metrics__swatch" style={{ background: s.color }} />
            <span className="vc-metrics__seg-label">{s.label}</span>
            <span className="vc-metrics__seg-ms">{s.ms.toLocaleString()} ms</span>
            <span className="vc-metrics__seg-pct">{Math.round((Math.max(0, s.ms) / segTotal) * 100)}%</span>
          </div>
        ))}
        <div className="vc-metrics__row vc-metrics__row--total">
          <span className="vc-metrics__swatch" />
          <span className="vc-metrics__seg-label">E2E</span>
          <span className="vc-metrics__seg-ms">{m.e2eMs.toLocaleString()} ms</span>
          <span className="vc-metrics__seg-pct">−{m.pipelineSavingMs.toLocaleString()} ms saved</span>
        </div>
      </div>
    </details>
  )
}
